'use client';

import { useTranslations } from 'next-intl';
import { useId, useMemo, useState } from 'react';
import { SEARCH_MIN_LENGTH, searchHaystack, searchTerm } from './search';
import type { OfferLocationsListResolved } from './types';
import { Link } from '@/components/Link';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { Text } from '@/lib/render/Text';

// CMS placeholder for the Standorte an offer is eligible at (10.02.12). The
// route resolves them server-side and hands them over through the render
// context, so the HTML the edge caches already carries the whole list — this
// widget never fetches. Outside `/angebote/{slug}/` there is no
// `offerLocations` entity and the widget renders nothing.
//
// The heading is the page's `<h1>`: the widget renders only on
// `/angebote/{slug}/`, whose Template Page has no Hero to own one.
//
// The rows arrive alphabetically by name (`OfferLocationsService`), which is
// the order the search box assumes: a visitor scans for the letter they would
// type.
//
// One `<table>` at every breakpoint, restyled into stacked rows below `md`,
// rather than a table plus a mobile list: with 800+ rows a second copy would
// double the markup every visitor downloads and hydrates.
export function OfferLocationsListComponent({
  resolved,
}: {
  resolved: OfferLocationsListResolved;
}) {
  const t = useTranslations('offerLocationsList');
  const { entities, substitutePlaceholders } = useRenderContext();
  const locations = entities.offerLocations;
  const [query, setQuery] = useState('');
  const searchId = useId();
  const hintId = useId();

  // One pass over the rows, not one per keystroke. 800+ Standorte is the
  // realistic size, so the filter below is a substring test per row and
  // nothing more.
  const haystacks = useMemo(
    () => (locations ?? []).map(searchHaystack),
    [locations],
  );

  const term = searchTerm(query);
  const matches = useMemo(
    () => haystacks.map((haystack) => !term || haystack.includes(term)),
    [haystacks, term],
  );
  const matchCount = matches.filter(Boolean).length;

  if (!locations) {
    return null;
  }

  return (
    <section className="pt-4">
      {resolved.title ? (
        <Text as="h1" field="title" className="text-center">
          {resolved.title}
        </Text>
      ) : (
        entities.offer && (
          <h1 className="text-center">
            {t('defaultTitle', {
              // Editor-typed, so it may carry placeholders (70.11).
              offerTitle: substitutePlaceholders(entities.offer.title),
            })}
          </h1>
        )
      )}

      {locations.length === 0 ? (
        <div className="pt-6 text-center">
          {resolved.emptyText && resolved.emptyText.length > 0 ? (
            <RichText nodes={resolved.emptyText} />
          ) : (
            <p>{t('emptyText')}</p>
          )}
        </div>
      ) : (
        <>
          <div className="pt-4 text-center">
            <label htmlFor={searchId} className="block font-medium">
              {t('searchLabel')}
            </label>
            {/* `type="search"` for the clear affordance and the right mobile
                keyboard; the filtering is ours, so the form never submits. */}
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-describedby={hintId}
              autoComplete="off"
              className="mt-2 w-full max-w-sm rounded border px-3 py-2"
            />
            <p id={hintId} className="my-0 pt-1 text-sm">
              {t('searchHint', { count: SEARCH_MIN_LENGTH })}
            </p>
          </div>

          {/* Announced, because the list it describes changes under a visitor
              who cannot see it move. */}
          <p role="status" className="my-0 pt-2 text-center">
            {term
              ? t('matchCount', { count: matchCount, total: locations.length })
              : t('count', { count: locations.length })}
          </p>

          {term && matchCount === 0 && (
            <p className="pt-4 text-center">{t('noMatches')}</p>
          )}

          {/* The wrapper carries the gap: a collapsed-border table ignores
              its own padding. */}
          <div className="pt-4">
            <table className="w-full border-collapse text-left max-md:block">
              {/* Visually hidden on mobile, where every cell carries its own
                  label, but kept for screen readers at every width. */}
              <thead className="max-md:sr-only">
                <tr className="border-b-2">
                  <th scope="col" className="px-3 py-2">
                    {t('columnName')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('columnAddress')}
                  </th>
                </tr>
              </thead>
              <tbody className="max-md:block">
                {locations.map((location, index) => (
                  <tr
                    key={location.shCenterId}
                    // 70.16: a filtered-out row stays in the response and is
                    // only hidden. `hidden` is `display: none !important`
                    // under Tailwind's preflight, so it beats the `block`
                    // utility beside it and drops the row out of the tab
                    // order and the accessibility tree in one move.
                    hidden={!matches[index]}
                    className="border-b max-md:block max-md:py-3"
                  >
                    <td className="px-3 py-2 font-medium max-md:block max-md:py-0">
                      <Link
                        href={`/nachhilfe/${location.slug}/`}
                        className="text-primary hover:underline"
                      >
                        {location.displayName}
                      </Link>
                    </td>
                    <td className="px-3 py-2 max-md:block max-md:py-0">
                      {[
                        location.street,
                        [location.postcode, location.city]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
